import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import DetectTodosModal from "@/components/DetectTodosModal";

/**
 * Notas / Tareas del proyecto (Mockup D #s9).
 *
 * 2 zonas en desktop, stacked en mobile:
 *   · Izquierda: Tareas (CRUD compacto tipo Linear, comparte API con OnTheTable).
 *   · Derecha:   Notas (texto + voz, persistidas en `project.project_notes[]`).
 *
 * Las tareas usan los endpoints `/api/inbox/tasks` (mismos que el Dashboard) con
 * `project_id` fijo. Cualquier cambio aquí se refleja en "Qué hay sobre la mesa".
 *
 * Adicionalmente: botón "✦ Detectar pendientes del guion (IA)" que invoca a
 * Claude para extraer marcas TODO/FALTA/REVISAR del guion subido o del editor.
 */
export default function NotesTab({ project, onChange, onSave }) {
  return (
    <section className="mx-auto max-w-6xl px-4 py-6 sm:px-8 sm:py-8" data-testid="notes-section">
      <div className="mb-6">
        <p className="gb-eyebrow">Bitácora</p>
        <h2 className="mt-1 gb-display text-2xl sm:text-3xl">Tareas y notas del proyecto</h2>
        <p className="mt-1 text-sm text-gb-muted">
          Lo que te ronda y lo que toca hacer. Las tareas también aparecen en "Qué hay sobre la mesa" del Dashboard.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
        <TasksPanel projectId={project.id} projectTitle={project.title} />
        <NotesPanel project={project} onChange={onChange} onSave={onSave} />
      </div>
    </section>
  );
}

// ─── Tasks panel ─────────────────────────────────────────────────────────

function TasksPanel({ projectId, projectTitle }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState("");
  const [newDue, setNewDue] = useState("");
  const [newPriority, setNewPriority] = useState("med");
  const [submitting, setSubmitting] = useState(false);
  const [detectOpen, setDetectOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inbox/tasks");
      setTasks((r.data || []).filter((t) => t.project_id === projectId));
    } catch (_e) {/* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  const create = async (e) => {
    e.preventDefault();
    if (!newText.trim()) return;
    setSubmitting(true);
    try {
      const r = await api.post("/inbox/tasks", {
        project_id: projectId,
        text: newText.trim(),
        due_date: newDue || "",
        priority: newPriority,
      });
      setTasks((ts) => [...ts, r.data]);
      setNewText("");
      setNewDue("");
      setNewPriority("med");
    } catch (_e) { /* ignore */ } finally {
      setSubmitting(false);
    }
  };

  const toggle = async (task) => {
    setTasks((ts) => ts.map((t) => (t.id === task.id ? { ...t, done: !t.done } : t)));
    try { await api.patch(`/inbox/tasks/${task.id}`, { project_id: projectId, done: !task.done }); }
    catch (_e) { load(); }
  };

  const remove = async (task) => {
    if (!window.confirm(`¿Eliminar "${task.text}"?`)) return;
    setTasks((ts) => ts.filter((t) => t.id !== task.id));
    try { await api.delete(`/inbox/tasks/${task.id}?project_id=${projectId}`); } catch (_e) { load(); }
  };

  const editText = async (task, text) => {
    setTasks((ts) => ts.map((t) => (t.id === task.id ? { ...t, text } : t)));
    try { await api.patch(`/inbox/tasks/${task.id}`, { project_id: projectId, text }); } catch (_e) { load(); }
  };

  const counts = useMemo(() => ({
    open: tasks.filter((t) => !t.done).length,
    done: tasks.filter((t) => t.done).length,
  }), [tasks]);

  return (
    <div className="rounded-2xl border border-gb-paper bg-gb-surface" data-testid="tasks-panel">
      <header className="flex items-center justify-between gap-2 border-b border-gb-paper px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-black">
          Tareas <span className="ml-1 text-gb-muted tabular-nums">{counts.open}</span>
          {counts.done > 0 && <span className="ml-1 text-[10px] text-gb-muted">· {counts.done} hechas</span>}
        </p>
        <button onClick={() => setDetectOpen(true)} data-testid="detect-todos-open"
                title="La IA lee el guion y extrae marcas TODO/FALTA/REVISAR"
                className="inline-flex items-center gap-1 rounded-full bg-gb-paper-soft px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-gb-red transition hover:bg-gb-red hover:text-white">
          ✦ Detectar IA
        </button>
      </header>

      {/* Composer */}
      <form onSubmit={create} className="border-b border-gb-paper bg-gb-paper-soft/30 px-4 py-3">
        <input value={newText} onChange={(e) => setNewText(e.target.value)}
               placeholder="+ Nueva tarea (Enter para crear)"
               data-testid="tasks-new-text"
               className="w-full rounded-md border border-gb-paper bg-white px-3 py-2 text-sm outline-none focus:border-gb-red" />
        <div className="mt-2 flex gap-2 text-xs">
          <input type="date" value={newDue} onChange={(e) => setNewDue(e.target.value)}
                 data-testid="tasks-new-due"
                 className="flex-1 rounded-md border border-gb-paper bg-white px-2 py-1.5 text-xs outline-none focus:border-gb-red" />
          <select value={newPriority} onChange={(e) => setNewPriority(e.target.value)}
                  data-testid="tasks-new-priority"
                  className="rounded-md border border-gb-paper bg-white px-2 py-1.5 text-xs outline-none focus:border-gb-red">
            <option value="low">Baja</option>
            <option value="med">Media</option>
            <option value="high">Alta</option>
          </select>
          <button type="submit" disabled={submitting || !newText.trim()}
                  data-testid="tasks-new-submit"
                  className="rounded-md bg-gb-red px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white hover-gb-red disabled:opacity-40">
            Añadir
          </button>
        </div>
      </form>

      {/* List */}
      <div>
        {loading && <p className="px-4 py-6 text-center text-sm text-gb-muted">Cargando…</p>}
        {!loading && tasks.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-gb-muted" data-testid="tasks-empty">
            Sin tareas todavía. Crea la primera arriba.
          </p>
        )}
        {tasks.map((t, idx) => (
          <TaskRow key={t.id} task={t} isLast={idx === tasks.length - 1}
                   onToggle={() => toggle(t)} onRemove={() => remove(t)}
                   onEditText={(text) => editText(t, text)} />
        ))}
      </div>

      {detectOpen && (
        <DetectTodosModal projectId={projectId} projectTitle={projectTitle}
                          onClose={() => setDetectOpen(false)}
                          onCreated={() => load()} />
      )}
    </div>
  );
}

const PRIORITY_COLORS = {
  low: { dot: "bg-stone-400", label: "baja" },
  med: { dot: "bg-amber-500", label: "media" },
  high: { dot: "bg-gb-red", label: "alta" },
};

function TaskRow({ task, isLast, onToggle, onRemove, onEditText }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(task.text);
  const prio = PRIORITY_COLORS[task.priority || "med"];
  const overdue = !task.done && task.due_date && new Date(task.due_date) < new Date();

  const commit = () => {
    setEditing(false);
    if (text.trim() && text !== task.text) onEditText(text.trim());
  };

  return (
    <div className={`group flex items-start gap-3 px-4 py-3 ${!isLast ? "border-b border-gb-paper" : ""} ${task.done ? "opacity-50" : ""}`}
         data-testid={`tt-task-${task.id}`}>
      <button onClick={onToggle} data-testid={`tt-toggle-${task.id}`}
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition ${
                task.done ? "border-emerald-600 bg-emerald-600 text-white" : "border-gb-paper hover:border-gb-red"
              }`}>
        {task.done && <span className="text-[11px] leading-none">✓</span>}
      </button>
      <div className="min-w-0 flex-1">
        {editing ? (
          <input value={text} onChange={(e) => setText(e.target.value)} autoFocus onBlur={commit}
                 onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setText(task.text); setEditing(false); }}}
                 data-testid={`tt-edit-${task.id}`}
                 className="w-full rounded border border-gb-red px-1.5 py-0.5 text-sm outline-none" />
        ) : (
          <p className={`cursor-text text-sm leading-snug ${task.done ? "line-through" : ""}`}
             onClick={() => setEditing(true)}>{task.text}</p>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gb-muted">
          <span className="inline-flex items-center gap-1">
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${prio.dot}`} />
            {prio.label}
          </span>
          {task.due_date && (
            <span className={overdue ? "font-semibold text-gb-red" : ""}>
              ◷ {new Date(task.due_date).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}
              {overdue && " · vencida"}
            </span>
          )}
        </div>
      </div>
      <button onClick={onRemove} data-testid={`tt-del-${task.id}`}
              className="opacity-0 transition group-hover:opacity-60 hover:text-gb-red">×</button>
    </div>
  );
}

// ─── Notes panel (texto + voz) ───────────────────────────────────────────

function NotesPanel({ project, onChange, onSave }) {
  const notes = project.project_notes || [];
  const setNotes = (next) => onChange({ ...project, project_notes: next });

  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  const nowIso = () => new Date().toISOString();

  const addTextNote = () => {
    const t = text.trim();
    if (!t) return;
    const note = { id: newId(), kind: "text", text: t, audio_url: "", duration_sec: 0,
                   created_at: nowIso(), updated_at: nowIso() };
    setNotes([note, ...notes]);
    setText("");
    setTimeout(() => onSave?.(), 100);
  };

  const startRecording = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mime = "audio/webm;codecs=opus";
      if (!MediaRecorder.isTypeSupported(mime)) {
        mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
             : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      }
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        await uploadAndTranscribe(blob, rec.mimeType || "audio/webm");
      };
      rec.start();
      mediaRef.current = rec;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (_e) {
      setError("No se pudo acceder al micrófono. Comprueba los permisos del navegador.");
    }
  };

  const stopRecording = () => {
    const rec = mediaRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    setRecording(false);
  };

  const uploadAndTranscribe = async (blob, mime) => {
    setTranscribing(true); setError("");
    try {
      const ext = mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm";
      const form = new FormData();
      form.append("audio", blob, `voice.${ext}`);
      const r = await api.post(`/icaa/projects/${project.id}/notes/audio`, form, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      });
      setNotes([r.data, ...notes]);
      setTimeout(() => onSave?.(), 100);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "No se pudo transcribir la nota.");
    } finally { setTranscribing(false); }
  };

  const updateNote = (id, patch) => setNotes(notes.map((n) => n.id === id ? { ...n, ...patch, updated_at: nowIso() } : n));
  const removeNote = (id) => {
    if (!window.confirm("¿Eliminar esta nota?")) return;
    setNotes(notes.filter((n) => n.id !== id));
    setTimeout(() => onSave?.(), 100);
  };

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      try { mediaRef.current.stop(); } catch (_e) { /* ignore */ }
    }
  }, []);

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="rounded-2xl border border-gb-paper bg-gb-surface" data-testid="notes-panel">
      <header className="flex items-center justify-between gap-2 border-b border-gb-paper px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-black">
          Notas <span className="ml-1 text-gb-muted tabular-nums">{notes.length}</span>
        </p>
      </header>

      {/* Composer */}
      <div className="border-b border-gb-paper bg-gb-paper-soft/30 px-4 py-3">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                  data-testid="note-text"
                  placeholder="Una idea, una intuición…"
                  className="w-full resize-y rounded-md border border-gb-paper bg-white p-2 text-sm leading-relaxed outline-none focus:border-gb-red" />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button onClick={addTextNote} disabled={!text.trim()} data-testid="note-add-text"
                  className="rounded-md bg-gb-black px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white hover:bg-gb-red disabled:opacity-40">
            Guardar nota
          </button>
          {!recording && !transcribing && (
            <button onClick={startRecording} data-testid="note-record-start"
                    className="flex items-center gap-1.5 rounded-md border border-gb-paper bg-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-gb-black hover:border-gb-red hover:text-gb-red">
              <span className="h-2 w-2 rounded-full bg-gb-red" /> Voz
            </button>
          )}
          {recording && (
            <button onClick={stopRecording} data-testid="note-record-stop"
                    className="flex items-center gap-1.5 rounded-md bg-gb-red px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white">
              <span className="h-2 w-2 animate-pulse rounded-sm bg-white" /> Detener · {fmtTime(elapsed)}
            </button>
          )}
          {transcribing && (
            <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-gb-red" data-testid="note-transcribing">
              <Spinner /> Transcribiendo…
            </span>
          )}
        </div>
        {error && <p className="mt-2 rounded bg-red-50 px-2 py-1 text-xs text-red-700" data-testid="note-error">{error}</p>}
      </div>

      {/* List */}
      <div className="max-h-[600px] overflow-y-auto">
        {notes.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-gb-muted" data-testid="notes-empty">
            Aún no hay notas. Apunta una idea o graba una nota de voz arriba.
          </p>
        )}
        {notes.map((n, idx) => {
          const isAudio = n.kind === "audio";
          return (
            <article key={n.id} data-testid={`note-${n.id}`}
                     className={`group relative border-l-[3px] px-4 py-3 ${
                       idx !== notes.length - 1 ? "border-b border-gb-paper" : ""
                     } ${isAudio ? "border-l-amber-500 bg-amber-50/30" : "border-l-emerald-600 bg-emerald-50/30"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.2em] ${
                  isAudio ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900"
                }`}>
                  {isAudio ? "Voz" : "Texto"}
                </span>
                <span className="text-[10px] text-gb-muted">
                  {new Date(n.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                </span>
                <button onClick={() => removeNote(n.id)} data-testid={`note-del-${n.id}`}
                        className="ml-auto opacity-0 transition group-hover:opacity-60 hover:text-gb-red">×</button>
              </div>
              {isAudio && n.audio_url && (
                <audio controls preload="none" data-testid={`note-audio-${n.id}`}
                       src={n.audio_url.startsWith("/api") ? `${API.replace('/api','')}${n.audio_url}` : n.audio_url}
                       className="mt-2 w-full" />
              )}
              <textarea value={n.text || ""}
                        onChange={(e) => updateNote(n.id, { text: e.target.value })}
                        onBlur={onSave} data-testid={`note-body-${n.id}`}
                        rows={Math.min(8, Math.max(2, Math.ceil((n.text || "").length / 70)))}
                        placeholder={isAudio ? "Transcripción editable…" : "Nota…"}
                        className="mt-2 w-full resize-y rounded-md border border-transparent bg-transparent p-1 text-sm leading-relaxed outline-none focus:border-gb-paper focus:bg-white" />
            </article>
          );
        })}
      </div>
    </div>
  );
}

const Spinner = () => (
  <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="40 60" />
  </svg>
);
